package wat.ui

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.*
import kotlin.js.Date

object DebugLog {
    private val logs = mutableListOf<String>()
    private var logContainer: HTMLDivElement? = null
    private var logContent: HTMLPreElement? = null

    fun init() {
        // Create floating log panel
        val panel = div {
            id = "debug-panel"
            style.cssText = "position:fixed;top:0;left:0;right:0;bottom:0;z-index:9999;background:rgba(0,0,0,0.95);display:none;flex-direction:column;font-family:'SF Mono',Consolas,monospace"
        }

        // Header bar
        panel.appendChild(div {
            style.cssText = "display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:rgba(255,255,255,0.05);border-bottom:1px solid rgba(255,255,255,0.1);flex-shrink:0"
            appendChild(div {
                style.cssText = "display:flex;align-items:center;gap:10px"
                appendChild(span("\uD83D\uDC1B") { style.cssText = "font-size:18px" })
                appendChild(span("TRACTOR Kotlin Debug Log") {
                    style.cssText = "font-size:14px;font-weight:800;color:#2dd4bf;letter-spacing:1px"
                })
                appendChild(span("") {
                    id = "log-count"
                    style.cssText = "font-size:11px;color:rgba(255,255,255,0.4);font-weight:600"
                })
            })
            appendChild(div {
                style.cssText = "display:flex;gap:8px"
                // Copy button
                appendChild(button("\uD83D\uDCCB Copy All") {
                    style.cssText = "padding:6px 14px;border-radius:8px;border:1.5px solid #2dd4bf40;background:#2dd4bf15;color:#2dd4bf;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit"
                    onclick = {
                        val text = logs.joinToString("\n")
                        window.navigator.clipboard.writeText(text).then({
                            this.textContent = "\u2705 Copied!"
                            window.setTimeout({ this.textContent = "\uD83D\uDCCB Copy All" }, 2000)
                        }, {
                            // Fallback: create textarea
                            val ta = document.createElement("textarea") as HTMLTextAreaElement
                            ta.value = text
                            ta.style.cssText = "position:fixed;left:-9999px"
                            document.body?.appendChild(ta)
                            ta.select()
                            document.execCommand("copy")
                            document.body?.removeChild(ta)
                            this.textContent = "\u2705 Copied!"
                            window.setTimeout({ this.textContent = "\uD83D\uDCCB Copy All" }, 2000)
                        })
                        Unit
                    }
                })
                // Clear button
                appendChild(button("\uD83D\uDDD1 Clear") {
                    style.cssText = "padding:6px 14px;border-radius:8px;border:1.5px solid rgba(255,255,255,0.15);background:rgba(255,255,255,0.05);color:rgba(255,255,255,0.5);font-size:12px;font-weight:700;cursor:pointer;font-family:inherit"
                    onclick = {
                        logs.clear()
                        logContent?.textContent = ""
                        updateCount()
                    }
                })
                // Close button
                appendChild(button("\u2715 Close") {
                    style.cssText = "padding:6px 14px;border-radius:8px;border:1.5px solid #fb718530;background:#fb718510;color:#fb7185;font-size:12px;font-weight:700;cursor:pointer;font-family:inherit"
                    onclick = {
                        panel.style.display = "none"
                        Unit
                    }
                })
            })
        })

        // Log content area
        val scrollArea = div {
            style.cssText = "flex:1;overflow-y:auto;padding:12px 16px"
        }
        val pre = document.createElement("pre") as HTMLPreElement
        pre.id = "log-content"
        pre.style.cssText = "margin:0;font-size:12px;line-height:1.8;color:rgba(255,255,255,0.85);white-space:pre-wrap;word-break:break-all;font-family:'SF Mono',Consolas,monospace"
        logContent = pre
        scrollArea.appendChild(pre)
        panel.appendChild(scrollArea)

        logContainer = panel as HTMLDivElement
        document.body?.appendChild(panel)

        // Re-render existing logs
        renderAll()
    }

    fun log(msg: String, level: String = "INFO") {
        val ts = Date().let {
            "${it.getHours().toString().padStart(2, '0')}:${it.getMinutes().toString().padStart(2, '0')}:${it.getSeconds().toString().padStart(2, '0')}.${it.getMilliseconds().toString().padStart(3, '0')}"
        }
        val prefix = when (level) {
            "ERROR" -> "\u274C ERROR"
            "WARN" -> "\u26A0\uFE0F WARN "
            "OK" -> "\u2705 OK   "
            "DB" -> "\uD83D\uDDC3\uFE0F DB   "
            "AUTH" -> "\uD83D\uDD10 AUTH "
            "NET" -> "\uD83C\uDF10 NET  "
            else -> "\u2139\uFE0F INFO "
        }
        val line = "[$ts] $prefix  $msg"
        logs.add(line)
        console.log("[TRACTOR-DBG] $line")

        // Append to visible log
        val pre = logContent ?: (document.getElementById("log-content") as? HTMLPreElement)
        if (pre != null) {
            val color = when (level) {
                "ERROR" -> "#fb7185"
                "WARN" -> "#fbbf24"
                "OK" -> "#2dd4bf"
                "DB" -> "#818cf8"
                "AUTH" -> "#c084fc"
                "NET" -> "#38bdf8"
                else -> "rgba(255,255,255,0.7)"
            }
            val span = document.createElement("span") as HTMLSpanElement
            span.style.color = color
            span.textContent = line + "\n"
            pre.appendChild(span)

            // Auto scroll to bottom
            pre.parentElement?.let { it.scrollTop = it.scrollHeight.toDouble() }
        }

        updateCount()
    }

    fun error(msg: String) = log(msg, "ERROR")
    fun ok(msg: String) = log(msg, "OK")
    fun db(msg: String) = log(msg, "DB")
    fun auth(msg: String) = log(msg, "AUTH")
    fun net(msg: String) = log(msg, "NET")
    fun warn(msg: String) = log(msg, "WARN")

    fun logJson(label: String, obj: dynamic) {
        try {
            val json = JSON.stringify(obj, null, 2)
            val truncated = if (json != null && json.toString().length > 500) json.toString().take(500) + "..." else json?.toString() ?: "null"
            log("$label: $truncated", "DB")
        } catch (e: Exception) {
            log("$label: [cannot stringify: ${e.message}]", "WARN")
        }
    }

    private fun renderAll() {
        val pre = logContent ?: return
        pre.innerHTML = ""
        logs.forEach { line ->
            val color = when {
                line.contains("\u274C") -> "#fb7185"
                line.contains("\u26A0") -> "#fbbf24"
                line.contains("\u2705") -> "#2dd4bf"
                line.contains("\uD83D\uDDC3") -> "#818cf8"
                line.contains("\uD83D\uDD10") -> "#c084fc"
                line.contains("\uD83C\uDF10") -> "#38bdf8"
                else -> "rgba(255,255,255,0.7)"
            }
            val span = document.createElement("span") as HTMLSpanElement
            span.style.color = color
            span.textContent = line + "\n"
            pre.appendChild(span)
        }
        updateCount()
    }

    private fun updateCount() {
        val el = document.getElementById("log-count")
        if (el != null) {
            val errors = logs.count { it.contains("\u274C") }
            el.textContent = "${logs.size} entries" + if (errors > 0) " / $errors errors" else ""
        }
    }

    fun getAll(): String = logs.joinToString("\n")
}
